import { trpcNoAuth } from '@prishapolicy/shared/trpc';
import {
  Button,
  ControlledCheckBox,
  DatePicker,
  Form,
  TextArea,
  TextInput,
  showErrorToast,
  showToast,
} from '@prishapolicy/shared/ui';
import { useFormikContext } from 'formik';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import { useState } from 'react';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon';
import CheckLineIcon from 'remixicon-react/CheckLineIcon';
import { date, object, string } from 'yup';

const validationSchema = object().shape({
  policyHolderName: string()
    .required('Policy holder name is required')
    .min(2, 'Policy holder name must be at least 2 characters'),
  policyNumber: string().required('Policy number is required'),
  dateOfIncident: date().required('Date of incident is required').nullable(),
  incidentHappened: string()
    .required('Incident details are required')
    .min(10, 'Incident details must be at least 10 characters'),
  isReportedPolice: string().required('This field is required').oneOf(['yes', 'no'], 'Please select either yes or no'),
  claimCater: string().required('This field is required').oneOf(['yes', 'no'], 'Please select either yes or no'),
});

const RaiseClaim = () => {
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const { mutateAsync: raiseAClaim } = trpcNoAuth['lead-management']['call-back-request']['submit-claim'].useMutation();
  return (
    <>
      <NextSeo
        title="Raise an Insurance Claim | PrishaPolicy"
        description="Submit an insurance claim request to PrishaPolicy and our client servicing team will help you with the next steps."
      />
      {!isRequestSubmitted ? (
        <div className="container mt-24 mb-8">
          <div className="rounded-large border-primary bg-surface-blue mb-6 border-l-[6px] p-6 shadow-lg">
            <h2 className="text-title-4 text-primary mb-2 font-bold">Project &amp; Engineering claim form</h2>
            <p className="text-grey-700 mb-3">
              Download the filled claim form now and share it with your team before proceeding with your claim
              submission.
            </p>
            <Link
              href="/resources/client-servicing-resources"
              className="border-primary text-body-4 text-primary hover:bg-primary inline-flex items-center rounded-md border-2 bg-white px-4 py-2 font-semibold hover:text-white"
            >
              Go to Client Servicing Resources
            </Link>
          </div>
          <Form
            initialValues={{
              policyHolderName: '',
              policyNumber: '',
              dateOfIncident: null,
              incidentHappened: '',
              isReportedPolice: 'no',
              claimCater: 'no',
            }}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              try {
                await raiseAClaim({ ...values });
                showToast({
                  title: 'Success',
                  description: 'Submitted Claim Request',
                });
              } catch (error) {
                showErrorToast(error);
              }

              setIsRequestSubmitted(true);
            }}
          >
            <div className="border-grey-200 mb-12 flex flex-col gap-4 rounded-lg border-[2px] p-6">
              <div className="flex items-center gap-3">
                <div className="border-grey-200 rounded-md border p-[9px]">
                  <ArrowLeftLineIcon className="text-grey-700" />
                </div>
                <h1 className="text-title-4 font-bold">Fill Up some of the details</h1>
              </div>

              <div className="flex flex-col gap-6"></div>
              <TextInput name={'policyHolderName'} label={'Policy Holder’s Name'} />
              <TextInput name={'policyNumber'} label={'Policy Number'} />
              <DatePicker name={'dateOfIncident'} label={'On what date incident occurred?'} />
              <TextArea name={'incidentHappened'} label={'In your own words, tell us what happened'} />
              <CheckBoxForm />
            </div>
            <Button variant="primary" text="Submit Claim Request" rightIcon="arrowRight" type="submit" />
          </Form>
        </div>
      ) : (
        <div className="flex h-[100vh] w-full flex-col items-center justify-center gap-3 text-center">
          <div className="bg-success rounded-full p-10">
            <CheckLineIcon className="text-title-6 h-10 w-10 font-bold text-white" />
          </div>
          <div>
            <h1 className="text-[40px] font-bold ">Claim Request Submitted</h1>
            <p className="text-title-5 text-center font-medium">We’ll get back to you shortly</p>
          </div>
          <h1 className="text-body-1 text-grey-700 font-normal">You can also give us a call on our numbers</h1>
          <div className="flex items-center gap-[10px]">
            <div className=" bg-surface-yellow rounded-md p-[9px]">
              <ArrowLeftLineIcon className="text-yellow-700" />
            </div>
            <h1 className="text-body-1 font-bold">
              <a href={`tel:${'+91 9935126030'}`}>+91 9935126030</a> |
              <a href={`tel:${'+91 9935126030'}`}> +91 9935126030</a>
            </h1>
          </div>
        </div>
      )}
    </>
  );
};

export default RaiseClaim;

export interface CheckBoxFormProps {
  isReportedPolice: string;
  claimCater: string;
}

export const CheckBoxForm = () => {
  // const { values } = useFormContext<CheckBoxFormProps>();
  const { values, setFieldValue } = useFormikContext<CheckBoxFormProps>();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-4">
        <h1 className="text-body-1 font-medium">
          Was the incident reported to police? <span className="text-danger">*</span>
        </h1>
        <div className="flex gap-3">
          <ControlledCheckBox
            name={'isReportedPolice'}
            value={'yes'}
            checked={values.isReportedPolice === 'yes'}
            onChange={() => setFieldValue('isReportedPolice', 'yes')}
            label="Yes"
          />
          <ControlledCheckBox
            name={'isReportedPolice'}
            value={'no'}
            checked={values.isReportedPolice === 'no'}
            onChange={() => setFieldValue('isReportedPolice', 'no')}
            label="No"
          />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <h1 className="text-body-1 font-medium">
          Does your claim cater to a third party liability <span className="text-danger">*</span>
        </h1>
        <div className="flex gap-3">
          <ControlledCheckBox
            name={'claimCater'}
            value={'yes'}
            checked={values.claimCater === 'yes'}
            onChange={() => setFieldValue('claimCater', 'yes')}
            label="Yes"
          />
          <ControlledCheckBox
            name={'claimCater'}
            value={'no'}
            checked={values.claimCater === 'no'}
            onChange={() => setFieldValue('claimCater', 'no')}
            label="No"
          />
        </div>
      </div>
    </div>
  );
};
